import { ChevronDown, HelpCircle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function getImageUrl(url) {
  if (!url) return "https://images.unsplash.com/photo-1530866495561-507c9faab2ed?auto=format&fit=crop&w=2070&q=80";
  if (url.startsWith("http")) return url;
  return `${BACKEND_URL}${url}`;
}


export default function HeroSection({ hero, general }) {
  const bgImage = getImageUrl(hero.background_image);

  // Stats dinámicas desde el CMS con valores por defecto
  const STATS = [
    {
      key: "edades",
      value: hero.stat_edades || "2013–2020",
      label: "Años de nacim.",
      sub: hero.stat_edades_sub || "6 a 13 años",
      color: "rgba(0,174,239,0.22)",
      border: "rgba(0,174,239,0.40)",
      valueColor: "#F5C518",
      colSpan: false,
    },
    {
      key: "semana1",
      value: hero.stat_semana1 || "22–26 Jun",
      label: "Semana 1",
      sub: null,
      color: "rgba(255,255,255,0.12)",
      border: "rgba(255,255,255,0.22)",
      valueColor: "#fff",
      colSpan: false,
    },
    {
      key: "semana2",
      value: hero.stat_semana2 || "29 Jun–3 Jul",
      label: "Semana 2",
      sub: null,
      color: "rgba(255,255,255,0.12)",
      border: "rgba(255,255,255,0.22)",
      valueColor: "#fff",
      colSpan: false,
    },
    {
      key: "horario",
      value: hero.stat_horario || "9:00 – 14:00h",
      label: "Horario base",
      sub: null,
      color: "rgba(60,180,75,0.22)",
      border: "rgba(60,180,75,0.38)",
      valueColor: "#fff",
      colSpan: false,
    },
    {
      key: "plazas",
      value: hero.stat_plazas || "Plazas limitadas",
      label: "¡Reserva ya!",
      sub: null,
      color: "rgba(245,130,32,0.28)",
      border: "rgba(245,130,32,0.48)",
      valueColor: "#F58220",
      colSpan: true,
      pulse: true,
    },
  ];

  return (
    <section
      id="inicio"
      className="relative flex items-center justify-center overflow-hidden"
      style={{
        minHeight: "100svh",
        backgroundImage: `url(${bgImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      {/* Overlay */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(150deg, rgba(0,114,164,0.90) 0%, rgba(0,60,100,0.84) 60%, rgba(0,114,164,0.72) 100%)",
        }}
      />

      {/* Decorative blurs */}
      <div
        className="absolute top-24 right-8 w-72 h-72 rounded-full opacity-20 pointer-events-none"
        style={{ background: "#F58220", filter: "blur(80px)" }}
      />
      <div
        className="absolute bottom-32 left-8 w-56 h-56 rounded-full opacity-20 pointer-events-none"
        style={{ background: "#3CB44B", filter: "blur(70px)" }}
      />

      {/* Content */}
      <div className="relative z-10 w-full max-w-5xl mx-auto px-6 md:px-12 text-center pt-28 pb-20 md:pt-36 md:pb-24 flex flex-col items-center">

        {/* Logo */}
        {general.logo_url ? (
          <div className="flex justify-center mb-8 animate-fade-up">
            <div
              className="rounded-full overflow-hidden shadow-2xl"
              style={{
                width: 100,
                height: 100,
                border: "3px solid rgba(255,255,255,0.55)",
                background: "rgba(255,255,255,0.97)",
                flexShrink: 0,
              }}
            >
              <img
                src={general.logo_url}
                alt="Logo Campus Verano Activo"
                style={{ width: "100%", height: "100%", objectFit: "contain", padding: "8px" }}
              />
            </div>
          </div>
        ) : (
          <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm border border-white/30 rounded-full px-5 py-2 mb-8 animate-fade-up">
            <div className="w-2 h-2 rounded-full bg-[#F5C518] animate-pulse" />
            <span className="text-white font-['Montserrat'] font-semibold text-sm">
              Organiza: {general.organization || "Asociación MuéveTe"}
            </span>
          </div>
        )}

        {/* Pill */}
        <div className="inline-block bg-[#F5C518] text-[#0F172A] font-['Montserrat'] font-bold px-6 py-2 rounded-full text-sm tracking-widest uppercase mb-6 animate-fade-up">
          {hero.subtitle || "La Estanca 2026"}
        </div>

        {/* Main heading */}
        <h1
          data-testid="hero-title"
          className="font-['Montserrat'] font-black text-white text-center mb-6 animate-fade-up-delay-1"
          style={{
            fontSize: "clamp(1.75rem, 3.5vw, 2.75rem)",
            lineHeight: 1.1,
            letterSpacing: "-0.02em",
            textShadow: "0 4px 24px rgba(0,0,0,0.30)",
          }}
        >
          {hero.title || "Campus Verano Activo"}
        </h1>

        {/* Tagline */}
        <p
          className="font-['Montserrat'] font-medium text-white/90 text-center mb-4 animate-fade-up-delay-2 max-w-2xl"
          style={{ fontSize: "clamp(0.875rem, 1.2vw, 1rem)", lineHeight: 1.6 }}
        >
          {hero.tagline || "La aventura del verano que tu hijo/a nunca olvidará"}
        </p>

        <p
          className="font-['Montserrat'] text-white/65 text-center mb-10 animate-fade-up-delay-3 max-w-xl"
          style={{ fontSize: "0.8125rem" }}
        >
          {hero.description ||
            "Kayak, Paddle Surf, BTT, Orientación y Realidad Aumentada en plena naturaleza."}
        </p>

        {/* CTAs */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-14 md:mb-20 animate-fade-up-delay-4">
          <a
            href="#inscripcion"
            data-testid="hero-register-cta"
            className="font-['Montserrat'] font-bold px-10 py-4 rounded-full text-sm font-semibold active:scale-95 transition-all w-full sm:w-auto text-center"
            style={{
              background: "#F5C518",
              color: "#0F172A",
              boxShadow: "0 8px 32px rgba(245,197,24,0.35)",
            }}
          >
            {hero.cta_text || "¡Inscríbete Ahora!"}
          </a>
          <a
            href="#faqs"
            data-testid="hero-faqs-cta"
            className="flex items-center justify-center gap-2 font-['Montserrat'] font-bold px-10 py-4 rounded-full text-sm font-semibold active:scale-95 transition-all w-full sm:w-auto"
            style={{
              background: "rgba(255,255,255,0.14)",
              color: "#fff",
              border: "2px solid rgba(255,255,255,0.35)",
              backdropFilter: "blur(8px)",
            }}
          >
            <HelpCircle size={18} />
            {hero.cta_whatsapp_text || "Saber más"}
          </a>
        </div>

        {/* Stats grid — 2 cols mobile, 5 cols desktop */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 md:gap-4 w-full animate-fade-up-delay-5">
          {STATS.map((stat) => (
            <div
              key={stat.key}
              data-testid={`hero-stat-${stat.key}`}
              className={`flex flex-col items-center justify-center rounded-2xl px-3 py-4 md:py-5 text-center backdrop-blur-md transition-transform hover:-translate-y-0.5 ${
                stat.colSpan ? "col-span-2 md:col-span-1" : ""
              }`}
              style={{
                background: stat.color,
                border: `1px solid ${stat.border}`,
              }}
            >
              {stat.pulse && (
                <div className="w-2 h-2 rounded-full bg-[#F58220] mb-2 animate-pulse" />
              )}
              <div
                className="font-['Montserrat'] font-bold leading-snug mb-1"
                style={{
                  color: stat.valueColor,
                  fontSize: "0.8rem",
                }}
              >
                {stat.value}
              </div>
              <div
                className="font-['Montserrat'] font-semibold uppercase tracking-wider text-white/60"
                style={{ fontSize: "0.6rem" }}
              >
                {stat.label}
              </div>
              {stat.sub && (
                <div
                  className="font-['Montserrat'] text-white/55 mt-0.5"
                  style={{ fontSize: "0.62rem" }}
                >
                  {stat.sub}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 animate-bounce">
        <ChevronDown size={28} color="rgba(255,255,255,0.55)" />
      </div>
    </section>
  );
}
